import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { FeedSourceListItem } from '@news-cms/shared';

interface Props {
  sources: FeedSourceListItem[];
  activeSourceId: string;
  onSelect: (id: string) => void;
}

/**
 * Single-row source tabs that scroll horizontally instead of wrapping to a
 * second line. Left/right chevrons appear only when there's overflow in that
 * direction; the active tab is scrolled into view when it changes. Count badges
 * stay visible (the reason we scroll rather than collapse into a dropdown).
 */
export function FeedSourceTabs({ sources, activeSourceId, onSelect }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    // 1px slack absorbs sub-pixel rounding so the arrow doesn't flicker at ends.
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
    // Re-measure when the number of tabs changes (source added/removed).
  }, [sources.length]);

  // Keep the selected tab visible even if it's scrolled off-screen.
  useEffect(() => {
    scrollRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [activeSourceId]);

  const scrollByDir = (dir: -1 | 1) => {
    scrollRef.current?.scrollBy({ left: dir * 220, behavior: 'smooth' });
  };

  return (
    <div className="relative border-b border-slate-200 dark:border-slate-800">
      {canLeft && (
        <button
          type="button"
          aria-label="เลื่อนซ้าย"
          onClick={() => scrollByDir(-1)}
          className="absolute bottom-px left-0 top-0 z-10 flex items-center bg-gradient-to-r from-white via-white to-transparent pl-1 pr-6 text-slate-400 hover:text-slate-700 dark:from-slate-950 dark:via-slate-950 dark:hover:text-slate-200"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label="แหล่งข้อมูล feed"
      >
        {sources.map((s) => {
          const isActive = s.id === activeSourceId;
          return (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-active={isActive}
              onClick={() => onSelect(s.id)}
              className={
                isActive
                  ? 'border-brand-600 text-brand-700 dark:text-brand-300 -mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium'
                  : '-mb-px shrink-0 whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }
            >
              {s.name}
              {s.pendingItemCount > 0 && (
                <span
                  className={
                    isActive
                      ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 ml-1.5 inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium'
                      : 'ml-1.5 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                  }
                >
                  {s.pendingItemCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {canRight && (
        <button
          type="button"
          aria-label="เลื่อนขวา"
          onClick={() => scrollByDir(1)}
          className="absolute bottom-px right-0 top-0 z-10 flex items-center bg-gradient-to-l from-white via-white to-transparent pl-6 pr-1 text-slate-400 hover:text-slate-700 dark:from-slate-950 dark:via-slate-950 dark:hover:text-slate-200"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
